import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Icon from '../components/Icon';
import Hero3D from '../three/Hero3D';
import HorizontalProjects from '../components/HorizontalProjects';
import { SectionHead, ArrowButton, StatsRow, CTABand } from '../components/ui';
import SplitText from '../motion/SplitText';
import Counter from '../motion/Counter';
import ScrollText from '../motion/ScrollText';
import Spotlight from '../motion/Spotlight';
import TiltCard from '../motion/TiltCard';
import VelocityMarquee from '../motion/VelocityMarquee';
import { ParallaxImage } from '../motion/Parallax';
import { Reveal, Stagger, StaggerItem, EASE } from '../motion/Reveal';
import useProjects from '../hooks/useProjects';
import { SERVICES, WHY_US, ABOUT_CARDS, PHASES, TICKER } from '../data/site';
import { ContractingGrid, EngagementModels } from '../components/Contracting';

function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 120]);
  const fade = useTransform(scrollY, [0, 450], [1, 0]);
  const stageY = useTransform(scrollY, [0, 700], [0, -60]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
    ref.current.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
  };

  return (
    <section className="hero" ref={ref} onPointerMove={onMove}>
      <div className="hero-bg" /><div className="grid-bg" />
      <div className="container hero-grid">
        <motion.div className="hero-copy" style={{ y, opacity: fade }}>
          <motion.span className="eyebrow" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease: EASE }}>
            <span className="dot" />PMC &amp; Contracting · Coimbatore
          </motion.span>
          <SplitText as="h1" className="display" delay={0.12} text="From foundation|to *handover,*|built right." />
          <motion.p className="lead" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7, ease: EASE }}>
            Project management consultancy and civil contracting under one roof — we plan, build and govern
            residential, commercial and layout projects with measurable, documented results.
          </motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7, ease: EASE }}>
            <ArrowButton to="/request-consultation">Request Consultation</ArrowButton>
            <ArrowButton to="/services" variant="outline">Our Services</ArrowButton>
          </motion.div>
          <Stagger className="hero-stats" delay={0.6} gap={0.1}>
            {[[10, '+', 'Years experience'], [360, '°', 'PMC + build'], [24, 'h', 'Response time']].map(([v, sfx, l]) => (
              <StaggerItem key={l} className="hero-stat">
                <strong><Counter to={v} suffix={sfx} duration={1.6} /></strong><span>{l}</span>
              </StaggerItem>
            ))}
          </Stagger>
        </motion.div>

        <motion.div className="hero-stage" style={{ y: stageY }}
          initial={{ opacity: 0, scale: 0.92, rotateX: 12 }} animate={{ opacity: 1, scale: 1, rotateX: 0 }}
          transition={{ delay: 0.2, duration: 1.3, ease: EASE }}>
          <div className="stage-halo" /><div className="stage-ring r1" /><div className="stage-ring r2" />
          <div className="stage-3d"><Hero3D /></div>
          <motion.div className="stage-chip glass sc-1" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8, ease: EASE }}>
            <span className="ico"><Icon name="shield" size={18} /></span><div><strong>QA / QC</strong><span>ISO-aligned process</span></div>
          </motion.div>
          <motion.div className="stage-chip glass sc-2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8, ease: EASE }}>
            <span className="ico"><Icon name="chart" size={18} /></span><div><strong>Daily DPRs</strong><span>Real-time site reporting</span></div>
          </motion.div>
          <motion.div className="stage-live" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }}>
            <span className="live-dot" />Managing &amp; building · 11 projects
          </motion.div>
        </motion.div>
      </div>
      <motion.div className="scroll-cue" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}><span className="mouse" />Scroll</motion.div>
    </section>
  );
}

function Lifecycle() {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.7', 'end 0.6'] });
  return (
    <section className="section">
      <div className="container">
        <div className="head-row">
          <SectionHead num="06" kicker="How we work" title="Leadership across|*every* phase" lead="Dedicated expertise governs every stage — from the first feasibility study to the final handover." />
          <ArrowButton to="/leadership" variant="outline">Our leadership model</ArrowButton>
        </div>
        <div className="timeline" ref={ref}>
          <div className="timeline-line"><motion.i style={{ scaleY: scrollYProgress }} /></div>
          {PHASES.map((p, i) => (
            <motion.div key={p.label} className={`tl-item${active >= i ? ' on' : ''}`} onViewportEnter={() => setActive((a) => Math.max(a, i))} viewport={{ amount: 0.6 }}>
              <span className="tl-dot">{i + 1}</span>
              <Reveal from="right" className="card card-hover tl-card">
                <div>
                  <span className="card-num">{p.label}</span>
                  <h3 className="h3" style={{ fontSize: '1.6rem', margin: '10px 0 12px' }}>{p.title}</h3>
                  <p>{p.desc}</p>
                  <div className="tag-row">{p.keywords.slice(0, 4).map((k) => <span className="tag" key={k}>{k}</span>)}</div>
                </div>
                <ParallaxImage src={p.image} alt={p.title} speed={0.1} />
              </Reveal>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Welcome() {
  const { projects } = useProjects();
  return (
    <>
      <Hero />
      <VelocityMarquee items={TICKER.slice(0, 8)} />

      {/* Statement + Stats Start */}
      <section className="section">
        <div className="container">
          <div className="statement">
            <Reveal from="none" className="kicker"><b>01</b>Who we are</Reveal>
            <ScrollText text="Blue Block is a project management consultancy and civil contractor with over a decade of experience — we govern your build as an independent PMC, or build it ourselves, so it stays on time, on budget and beyond standard." />
          </div>
          <StatsRow />
        </div>
      </section>
      {/* Statement + Stats End */}

      {/* Services Start */}
      <section className="section section-dark" style={{ overflow: 'hidden' }}>
        <div className="aurora" /><div className="grid-bg" />
        <div className="container" style={{ position: 'relative' }}>
          <div className="head-row">
            <SectionHead num="02" kicker="PMC services" title="Everything your build|needs, *governed.*" lead="Full-cycle project management spanning planning, execution, quality control and handover — for your contractor or ours." />
            <ArrowButton to="/services" variant="ghost-light">All services</ArrowButton>
          </div>
          <Stagger className="svc-grid" gap={0.09}>
            {SERVICES.map((s, i) => (
              <StaggerItem key={s.title}>
                <Spotlight className="svc-card">
                  <div className="num">0{i + 1}<Icon name="arrow" size={20} /></div>
                  <div className="icon-box"><Icon name={s.icon} size={24} /></div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <div className="tag-row">{s.tags.map((t) => <span className="tag" key={t}>{t}</span>)}</div>
                </Spotlight>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      {/* Services End */}

      <ContractingGrid num="03" />

      <EngagementModels num="04" alt />

      <HorizontalProjects projects={projects} num="05" />

      <Lifecycle />

      {/* Why us — Bento Start */}
      <section className="section section-alt">
        <div className="container">
          <SectionHead center num="07" kicker="Why Blue Block" title="Your trusted|*PMC &amp; build partner*" lead="A decade of managing and constructing projects with measurable, documented results." />
          <Stagger className="bento" gap={0.08}>
            <StaggerItem className="b-image b-tall3">
              <ParallaxImage src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=900&h=1300&fit=crop&q=68&auto=format" alt="Engineers reviewing plans" speed={0.12} />
              <div className="overlay" />
              <div className="cap"><strong>10+</strong>Years of trusted delivery across Tamil Nadu</div>
            </StaggerItem>
            {WHY_US.map((w, i) => (
              <StaggerItem key={w.title} className={[0, 3, 5].includes(i) ? 'b-wide' : ''}>
                <TiltCard className={`card${i === 3 ? ' b-blue' : ''}`} max={7}>
                  <div className="icon-box"><Icon name={w.icon} size={24} /></div>
                  <h3 className="h3">{w.title}</h3>
                  <p>{w.desc}</p>
                </TiltCard>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      {/* Why Us - Bento End  */}

      {/* The Blue Block Standard Start */}
      <section className="section">
        <div className="container">
          <SectionHead num="08" kicker="Our standard" title="Built on trust,|delivered with *rigour*" />
          <Stagger className="about-row" gap={0.12}>
            {ABOUT_CARDS.map((c, i) => (
              <StaggerItem key={c.title} className="about-cell">
                <span className="card-num">0{i + 1} —</span>
                <h3>{c.title}</h3>
                <p>{c.body}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      {/* The Blue Block Standard End */}

      <CTABand />
    </>
  );
}
